/**
 * ThemeProvider — aplica tema light/dark via `[data-theme]` no <html>.
 *
 * Prepara white-label: expõe `branding` (accent color, logo URL) consumido
 * via useTheme(). O fetch de `/v1/tenants/{id}/branding` é um ponto de
 * extensão — retorna o branding default enquanto o backend não estiver pronto.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";

export type Theme = "light" | "dark";

export interface Branding {
  /** Cor de acento (CSS color válida) */
  accentColor: string;
  /** URL do logo do tenant (se personalizado) */
  logoUrl: string | null;
  /** Nome do tenant */
  tenantName: string;
}

const DEFAULT_BRANDING: Branding = {
  accentColor: "#369DA0",
  logoUrl: null,
  tenantName: "Vellus Brasil",
};

export interface ThemeContextValue {
  theme: Theme;
  toggleTheme(): void;
  setTheme(t: Theme): void;
  branding: Branding;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme deve ser usado dentro de ThemeProvider");
  return ctx;
}

const STORAGE_KEY = "azim.theme";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as Theme | null;
    return saved === "dark" || saved === "light" ? saved : "light";
  });

  // Branding: começa com o default; quando o backend estiver pronto,
  // chame GET /v1/tenants/{id}/branding e chame setBranding(data).
  const [branding] = useState<Branding>(DEFAULT_BRANDING);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(STORAGE_KEY, theme);
    // White-label: injeta CSS var de acento se customizado
    if (branding.accentColor !== DEFAULT_BRANDING.accentColor) {
      document.documentElement.style.setProperty("--bg-accent", branding.accentColor);
    }
  }, [theme, branding.accentColor]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggleTheme = useCallback(
    () => setThemeState((t) => (t === "light" ? "dark" : "light")),
    []
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, toggleTheme, setTheme, branding }),
    [theme, toggleTheme, setTheme, branding]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
